if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        /*
         * DIAMENTI – MOBILE LIGHTBOX SWIPE STATE
         */
        this.diamentiTouchStartX = null;
        this.diamentiTouchStartY = null;

        this.addEventListener(
          'touchstart',
          this.onDiamentiTouchStart.bind(this),
          { passive: true }
        );

        this.addEventListener(
          'touchend',
          this.onDiamentiTouchEnd.bind(this),
          { passive: true }
        );

        this.addEventListener(
          'touchcancel',
          this.resetDiamentiTouch.bind(this),
          { passive: true }
        );
      }


      hide() {
        /*
         * Restore normal page state when the lightbox closes.
         */
        document.body.classList.remove(
          'diamenti-lightbox-open'
        );

        this.resetDiamentiTouch();

        super.hide();
      }


      show(opener) {
        super.show(opener);

        /*
         * Mark the page as having an open Diamenti lightbox.
         *
         * CSS uses this state to hide floating UI such as
         * Shopify Chat without modifying the chat app itself.
         */
        document.body.classList.add(
          'diamenti-lightbox-open'
        );

        this.showActiveMedia();

        /*
         * Create/update custom controls on mobile only.
         */
        if (
          window.matchMedia(
            '(max-width: 749px)'
          ).matches
        ) {
          this.ensureDiamentiControls();
          this.updateDiamentiProgress();
        }
      }


      showActiveMedia() {
        if (!this.openedBy) return;

        const mediaId =
          this.openedBy.getAttribute(
            'data-media-id'
          );

        if (!mediaId) return;

        /*
         * Remove active state from all media except
         * the media that opened the modal.
         */
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${mediaId}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });

        const activeMedia =
          this.querySelector(
            `[data-media-id="${mediaId}"]`
          );

        if (!activeMedia) return;

        const activeMediaTemplate =
          activeMedia.querySelector('template');

        const activeMediaContent =
          activeMediaTemplate
            ? activeMediaTemplate.content
            : null;

        activeMedia.classList.add('active');

        const container =
          this.querySelector(
            '[role="document"]'
          );


        /*
         * DESKTOP
         *
         * Preserve Whisper's original modal behaviour.
         */
        if (
          window.matchMedia(
            '(min-width: 750px)'
          ).matches
        ) {
          activeMedia.scrollIntoView();

          if (container) {
            container.scrollLeft =
              (
                activeMedia.width -
                container.clientWidth
              ) / 2;
          }
        }


        /*
         * MOBILE
         *
         * Do not use Whisper's desktop scroll positioning.
         * Our mobile lightbox CSS controls the image position.
         */
        else if (container) {
          container.scrollTop = 0;
          container.scrollLeft = 0;
        }


        /*
         * Preserve Whisper's original deferred
         * YouTube loading behaviour.
         */
        if (
          activeMedia.nodeName ==
            'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector(
            '.js-youtube'
          )
        ) {
          activeMedia.loadContent();
        }
      }


      /*
       * =====================================================
       * DIAMENTI – LIGHTBOX CONTROLS
       * =====================================================
       */

      ensureDiamentiControls() {
        /*
         * Don't create duplicate controls if the modal
         * has already been opened previously.
         */
        if (
          this.querySelector(
            '.diamenti-lightbox-controls'
          )
        ) {
          return;
        }

        const dialog =
          this.querySelector(
            '.product-media-modal__dialog'
          );

        if (!dialog) return;


        /*
         * Main controls container.
         */
        const controls =
          document.createElement('div');

        controls.className =
          'diamenti-lightbox-controls';

        controls.setAttribute(
          'aria-hidden',
          'true'
        );


        /*
         * Progress track.
         */
        const progress =
          document.createElement('div');

        progress.className =
          'diamenti-lightbox-progress';


        /*
         * Active progress segment.
         */
        const bar =
          document.createElement('span');

        bar.className =
          'diamenti-lightbox-progress__bar';


        progress.appendChild(bar);
        controls.appendChild(progress);
        dialog.appendChild(controls);
      }


      /*
       * Return the modal media container.
       */
      getDiamentiContainer() {
        return this.querySelector(
          '.product-media-modal__content'
        );
      }


      /*
       * Return photographs only.
       *
       * Videos and 3D models deliberately remain outside
       * the custom photograph swipe sequence.
       */
      getDiamentiImages() {
        const container =
          this.getDiamentiContainer();

        if (!container) return [];

        return Array.from(
          container.children
        ).filter(
          (element) =>
            element.tagName === 'IMG' &&
            element.hasAttribute(
              'data-media-id'
            )
        );
      }


      /*
       * Find the currently visible photograph.
       */
      getDiamentiActiveImage() {
        return (
          this.getDiamentiImages().find(
            (image) =>
              image.classList.contains(
                'active'
              )
          ) || null
        );
      }


      /*
       * =====================================================
       * DIAMENTI – PROGRESS INDICATOR
       * =====================================================
       */

      updateDiamentiProgress() {
        const images =
          this.getDiamentiImages();

        const activeImage =
          this.getDiamentiActiveImage();

        const bar =
          this.querySelector(
            '.diamenti-lightbox-progress__bar'
          );

        const controls =
          this.querySelector(
            '.diamenti-lightbox-controls'
          );

        if (!bar || !controls) return;


        /*
         * Don't show navigation when there is only
         * one photograph.
         */
        if (
          images.length <= 1 ||
          !activeImage
        ) {
          controls.style.display = 'none';
          return;
        }

        controls.style.display = 'flex';


        const currentIndex =
          images.indexOf(activeImage);

        if (currentIndex === -1) return;


        /*
         * Divide the progress track equally between
         * however many photographs the product has.
         */
        const segmentWidth =
          100 / images.length;

        bar.style.width =
          `${segmentWidth}%`;


        /*
         * Move the active segment one complete segment
         * for every photograph.
         */
        bar.style.transform =
          `translateX(${currentIndex * 100}%)`;
      }


      /*
       * =====================================================
       * DIAMENTI – IMAGE CHANGE
       * =====================================================
       */

      setDiamentiActiveImage(image) {
        if (!image) return;

        const images =
          this.getDiamentiImages();


        /*
         * Remove active state from all photographs.
         */
        images.forEach((item) => {
          item.classList.remove('active');
        });


        /*
         * Show the requested photograph.
         */
        image.classList.add('active');


        /*
         * Keep the mobile stage anchored at its origin.
         */
        const container =
          this.getDiamentiContainer();

        if (container) {
          container.scrollTop = 0;
          container.scrollLeft = 0;
        }


        /*
         * Move the progress indicator.
         */
        this.updateDiamentiProgress();
      }


      /*
       * Change to the photograph immediately before
       * or after the active photograph.
       *
       * direction:
       *  1 = next
       * -1 = previous
       */
      showDiamentiAdjacentImage(direction) {
        const images =
          this.getDiamentiImages();

        if (images.length < 2) return;


        const activeImage =
          this.getDiamentiActiveImage();

        if (!activeImage) return;


        const currentIndex =
          images.indexOf(activeImage);

        if (currentIndex === -1) return;


        const nextIndex =
          currentIndex + direction;


        /*
         * No infinite looping.
         *
         * Swiping backwards on the first photograph or
         * forwards on the final photograph does nothing.
         */
        if (
          nextIndex < 0 ||
          nextIndex >= images.length
        ) {
          return;
        }


        this.setDiamentiActiveImage(
          images[nextIndex]
        );
      }


      /*
       * =====================================================
       * DIAMENTI – MOBILE SWIPE
       * =====================================================
       */

      onDiamentiTouchStart(event) {
        /*
         * Custom swipe behaviour is mobile only.
         */
        if (
          !window.matchMedia(
            '(max-width: 749px)'
          ).matches
        ) {
          return;
        }


        /*
         * Only operate while the product modal is open.
         */
        if (!this.hasAttribute('open')) {
          return;
        }


        /*
         * Ignore multi-touch gestures.
         *
         * This prevents pinch gestures from being mistaken
         * for image navigation.
         */
        if (event.touches.length !== 1) {
          this.resetDiamentiTouch();
          return;
        }


        /*
         * ROBUSTNESS:
         *
         * Only begin a swipe when the gesture starts inside
         * the actual image stage.
         *
         * Gestures beginning on the close button or progress
         * controls therefore cannot accidentally change image.
         */
        const container =
          this.getDiamentiContainer();

        if (
          !container ||
          !container.contains(event.target)
        ) {
          this.resetDiamentiTouch();
          return;
        }


        const activeImage =
          this.getDiamentiActiveImage();

        if (!activeImage) {
          this.resetDiamentiTouch();
          return;
        }


        const touch =
          event.touches[0];

        this.diamentiTouchStartX =
          touch.clientX;

        this.diamentiTouchStartY =
          touch.clientY;
      }


      onDiamentiTouchEnd(event) {
        /*
         * No valid starting point means there is
         * no swipe to evaluate.
         */
        if (
          this.diamentiTouchStartX === null ||
          this.diamentiTouchStartY === null
        ) {
          this.resetDiamentiTouch();
          return;
        }


        if (
          event.changedTouches.length !== 1
        ) {
          this.resetDiamentiTouch();
          return;
        }


        const touch =
          event.changedTouches[0];


        const deltaX =
          touch.clientX -
          this.diamentiTouchStartX;

        const deltaY =
          touch.clientY -
          this.diamentiTouchStartY;


        const horizontalDistance =
          Math.abs(deltaX);

        const verticalDistance =
          Math.abs(deltaY);


        /*
         * Reset immediately so every gesture starts
         * from a clean state.
         */
        this.resetDiamentiTouch();


        /*
         * Require a deliberate horizontal swipe.
         *
         * Minimum movement = 50px.
         *
         * Horizontal movement must also clearly dominate
         * vertical movement. This prevents normal vertical
         * finger movement from changing photographs.
         */
        if (
          horizontalDistance < 50 ||
          horizontalDistance <=
            verticalDistance * 1.2
        ) {
          return;
        }


        /*
         * Finger moves LEFT  -> next photograph.
         * Finger moves RIGHT -> previous photograph.
         */
        this.showDiamentiAdjacentImage(
          deltaX < 0 ? 1 : -1
        );
      }


      /*
       * Clear all stored touch coordinates.
       */
      resetDiamentiTouch() {
        this.diamentiTouchStartX = null;
        this.diamentiTouchStartY = null;
      }
    }
  );
}

/* =========================================================
   DIAMENTI – MOBILE PRODUCT GALLERY PAGINATION
   Dynamic square indicators
   ========================================================= */

(() => {

  const MOBILE_QUERY = '(max-width: 749px)';


  /*
    =====================================================
    INITIALISE PAGINATION
    =====================================================
  */

  function initialiseDiamentiGalleryPagination() {

    /*
      Custom pagination is mobile only.
    */
    if (!window.matchMedia(MOBILE_QUERY).matches) {
      return;
    }


    /*
      Find the main product media galleries.
    */
    const galleries =
      document.querySelectorAll(
        '.product__media-wrapper media-gallery'
      );


    galleries.forEach((gallery) => {

      /*
        Prevent duplicate pagination if Shopify
        reinitialises or reloads the section.
      */
      if (
        gallery.dataset.diamentiPaginationReady === 'true'
      ) {
        return;
      }


      /*
        Find Whisper's existing gallery controls.
      */
      const controls =
        gallery.querySelector(
          '.diamenti-gallery-controls'
        );


      /*
        Shopify's native current-slide counter.

        Example:
        1
      */
      const currentCounter =
        gallery.querySelector(
          '.slider-counter--current'
        );


      /*
        Shopify's native total-slide counter.

        Example:
        3
      */
      const totalCounter =
        gallery.querySelector(
          '.slider-counter--total'
        );


      /*
        Stop safely if the required Whisper
        gallery elements cannot be found.
      */
      if (
        !controls ||
        !currentCounter ||
        !totalCounter
      ) {
        return;
      }


      /*
        Read the total number of gallery items.
      */
      const total =
        parseInt(
          totalCounter.textContent.trim(),
          10
        );


      if (
        !Number.isFinite(total) ||
        total < 1
      ) {
        return;
      }


      /*
        Products containing only one media item
        do not need pagination.
      */
      if (total <= 1) {

        gallery.dataset.diamentiPaginationReady =
          'true';

        return;
      }


      /*
        =====================================================
        CREATE PAGINATION CONTAINER
        =====================================================
      */

      const pagination =
        document.createElement('div');


      pagination.className =
        'diamenti-gallery-pagination';


      /*
        The squares are visual.

        Shopify's original hidden numerical counter
        continues to provide the underlying slider state.
      */
      pagination.setAttribute(
        'aria-hidden',
        'true'
      );


      const indicators = [];


      /*
        =====================================================
        CREATE ONE SQUARE PER MEDIA ITEM
        =====================================================
      */

      for (
        let index = 0;
        index < total;
        index += 1
      ) {

        const indicator =
          document.createElement('span');


        indicator.className =
          'diamenti-gallery-pagination__item';


        pagination.appendChild(
          indicator
        );


        indicators.push(
          indicator
        );
      }


      /*
        Insert the custom pagination into Whisper's
        existing gallery controls.
      */
      controls.appendChild(
        pagination
      );


      /*
        =====================================================
        UPDATE ACTIVE SQUARE
        =====================================================
      */

      const updatePagination = () => {

        /*
          Read Shopify's current slide number.
        */
        const current =
          parseInt(
            currentCounter.textContent.trim(),
            10
          );


        if (
          !Number.isFinite(current) ||
          current < 1
        ) {
          return;
        }


        /*
          Activate the corresponding square.

          Shopify counts from 1.
          JavaScript arrays count from 0.

          Therefore:
          current - 1
        */
        indicators.forEach(
          (indicator, index) => {

            indicator.classList.toggle(
              'is-active',
              index === current - 1
            );

          }
        );
      };


      /*
        Set the correct active indicator immediately.
      */
      updatePagination();


      /*
        =====================================================
        WATCH SHOPIFY'S EXISTING SLIDER STATE
        =====================================================

        Whisper already updates:
        .slider-counter--current

        whenever the customer changes the gallery image.

        We watch that existing value instead of creating
        a separate carousel/navigation system.
      */

      const observer =
        new MutationObserver(
          updatePagination
        );


      observer.observe(
        currentCounter,
        {
          childList: true,
          characterData: true,
          subtree: true,
        }
      );


      /*
        Mark this gallery as successfully initialised.
      */
      gallery.dataset.diamentiPaginationReady =
        'true';

    });
  }


  /*
    =====================================================
    NORMAL PAGE LOAD
    =====================================================
  */

  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initialiseDiamentiGalleryPagination
    );

  } else {

    initialiseDiamentiGalleryPagination();

  }


  /*
    =====================================================
    SHOPIFY THEME EDITOR SUPPORT
    =====================================================

    Shopify can reload sections without reloading
    the entire browser page.

    Initialise the pagination again when required.
  */

  document.addEventListener(
    'shopify:section:load',
    initialiseDiamentiGalleryPagination
  );

})();